import { Dtos, proConRelationshipId, type SuretyAccountId } from "@inrev/common";
import { DateTime } from "luxon";
import { useRequest } from "../../../utils/request";
import { proConRelationshipsQueryKey } from "../decisioning.keys";
import { replaceById } from "../optimistic";
import { type ProConRelationship } from "../types";
import { useOptimisticListMutation } from "../useOptimisticListMutation";

export const useCreateProConRelationship = (accountId: SuretyAccountId) => {
	const { post } = useRequest();

	const mutation = useOptimisticListMutation<
		ProConRelationship,
		Dtos.Decisioning.ProConRelationship.Create.Request,
		ProConRelationship
	>({
		queryKey: proConRelationshipsQueryKey(accountId),
		request: async (request) =>
			Dtos.Decisioning.ProConRelationship.Get.Response.schema.parse(
				await post<unknown>(
					`/v2/admin/surety/accounts/${accountId}/pro-con-relationships`,
					request,
				),
			),
		apply: (items, request, pendingId) => {
			const now = DateTime.now();
			return [
				...items,
				{
					id: proConRelationshipId.parse(pendingId),
					accountId,
					sourceProConId: request.sourceProConId,
					targetProConId: request.targetProConId,
					type: request.type,
					rationale: request.rationale,
					createdAt: now,
					updatedAt: now,
				},
			];
		},
		affectedIds: (_request, pendingId) => [pendingId],
		failureMessage: () => "Couldn't connect the factors.",
		creates: true,
		reconcile: (items, created, _request, pendingId) => replaceById(items, pendingId, created),
	});

	return {
		createRelationship: mutation.mutate,
		/** Resolves once the server confirms, so a confirmation can wait for it; rejects on failure. */
		createRelationshipAsync: mutation.mutateAsync,
	};
};
